import dayjs from "dayjs";
import { useRef, useState } from "react";
import _ from "underscore";
import { DateRangePicker } from "rsuite";

import useToggle from "../../hooks/useToggle";
import FeedItemDrawer from "../Drawers/FeedItemDrawer";
import FeedItem from "../Feed/FeedItem";
import CreatedSortByButton from "../Feed/CreatedSortByButton";
import useClouds from "../../api/api-hooks/cloud/useClouds";
import usePeople from "../../api/api-hooks/cloud/usePeople";
import useQueriedRecords from "../../api/api-hooks/records/useQueriedRecords";
import { Query } from "../../types/GlobalInterfaces";
import { FeedEvent } from "../../types/FeedInterfaces";
import AppEmptyState from "../../elements/AppEmptyState";

const getDefaultQuery: (recordID: string) => Query = (recordID: string) => ({
  Fields: ["*"],
  Filters: [
    {
      Group: true,
      Filters: [
        { Field: "RelatedTo", Method: "equals", Value: recordID },
        { Field: "Type", Method: "not-equals", Value: "Security" },
      ],
    },
  ],
  OrderBy: {
    Field: "Created",
    Direction: "DESC",
  },
  Pagination: {
    Count: 50,
    Page: 1,
  },
});

const RecordFeed = ({ recordID }: { recordID: string }) => {
  const [query, setQuery] = useState(() => getDefaultQuery(recordID));
  const [feedItem, setFeedItem] = useState<FeedEvent | null>(null);
  const [feedItemDrawer, toggleFeedItemDrawer] = useToggle();
  const [dateRange, setDateRange] = useState(null);
  const { people, peopleLoading } = usePeople();
  const { clouds, cloudsLoading } = useClouds();
  const { records, pagination, recordsLoading } = useQueriedRecords({
    moduleAPIName: "Feed",
    query: query,
  });
  const mappedPeople = useRef<Record<string, string>>({});
  const mappedClouds = useRef<Record<string, string>>({});

  const getUserName = (userID: string) => {
    if (!userID) return "";

    if (mappedPeople.current?.[userID]) {
      return mappedPeople.current[userID];
    } else {
      const foundUser = _.findWhere(people, { id: userID });

      mappedPeople.current[userID] =
        foundUser?.Name ?? foundUser?.PrimaryContactEmail ?? "Deleted User";

      return mappedPeople.current[userID];
    }
  };

  const getCloudName = (cloudID: string) => {
    if (mappedClouds.current?.[cloudID]) {
      return mappedClouds.current[cloudID];
    } else {
      const foundCloud = _.findWhere(clouds || [], { TenantID: cloudID });

      mappedClouds.current[cloudID] = foundCloud?.TenantName ?? "Deleted Cloud";

      return mappedClouds.current[cloudID];
    }
  };

  const openFeedItem = (item: any) => {
    setFeedItem(item);
    toggleFeedItemDrawer();
  };

  const handleDateRangeChange = (dateRange: any) => {
    setDateRange(dateRange);
    const newQuery = getDefaultQuery(recordID);

    if (dateRange === null) {
      return setQuery(newQuery);
    }

    const newFilter = {
      Field: "Created",
      Method: "between",
      Min: dayjs(dateRange[0]).toISOString(),
      Max: dayjs(dateRange[1]).toISOString(),
    };

    newQuery.Filters[0].Filters!.push(newFilter);
    setQuery(newQuery);
  };

  const LoadingFeed = () => (
    <>
      <div className="skeleton tw:my-2 tw:mx-4" style={{ height: 40 }}></div>
      <div className="skeleton tw:my-2 tw:mx-4" style={{ height: 40 }}></div>
      <div className="skeleton tw:my-2 tw:mx-4" style={{ height: 40 }}></div>
      <div className="skeleton tw:my-2 tw:mx-4" style={{ height: 40 }}></div>
    </>
  );

  return (
    <div className="tw:flex-col tw:w-full">
      <div
        className="tw:flex tw:justify-between tw:items-center"
        style={{ paddingLeft: "1.5rem", width: "100%" }}
      >
        <h3 className="tw:mb-0">Record History</h3>
        <div>
          <CreatedSortByButton query={query} setQuery={setQuery} />
          <DateRangePicker
            className="mr-4"
            size="sm"
            placeholder="Select Date Range"
            placement="autoVerticalEnd"
            value={dateRange}
            onChange={(val) => handleDateRangeChange(val)}
          />
        </div>
      </div>
      <hr className="tw:mx-4 tw:mt-2 tw:mb-4"></hr>
      <div className="tw:w-full">
        {peopleLoading || recordsLoading || cloudsLoading ? (
          <LoadingFeed />
        ) : (
          <div className="tw:pl-3 tw:relative">
            {records.length > 1 && (
              <div
                className="tw:absolute"
                style={{
                  borderLeft: "3px solid var(--light)",
                  height: "70%",
                  left: 27,
                  top: 20,
                  zIndex: -1,
                }}
              ></div>
            )}
            {records.length > 0 ? (
              records.map((record: any) => {
                return (
                  <FeedItem
                    key={record.id}
                    record={record}
                    getUserName={getUserName}
                    getCloudName={getCloudName}
                    onOpen={() => openFeedItem(record)}
                  />
                );
              })
            ) : (
              <AppEmptyState
                icon="mdi-calendar-alert"
                title="No History"
                description="No history has been added yet"
              />
            )}
          </div>
        )}
      </div>
      <FeedItemDrawer
        open={feedItemDrawer}
        onClose={() => {
          toggleFeedItemDrawer();
          setFeedItem(null);
        }}
        feedRecord={feedItem}
        userName={getUserName(feedItem ? feedItem?.UserID : "")}
      />
    </div>
  );
};

export default RecordFeed;
